import { useState, useEffect } from 'react';
import 'rxjs/add/operator/distinctUntilChanged';

import type { ModuleName, Modules, AsyncModules } from './types';
import moduleLocator from 'moduleLocator';

import {
    loadBetslipModule,
    loadRestrictionModule,
} from 'modules/betslip/async';
import { loadBetsHistoryModule } from 'modules/bets-history/async';
import { loadMatchModule } from 'modules/match/async';
import { loadFreebetModule } from 'modules/freebet/async';
import { loadSearchModule } from 'modules/search/async';
import { loadStreamModule } from 'modules/stream/async';
import { loadFiltersModule } from 'modules/filters/async';
import { loadOutrightModule } from 'modules/outright/async';

export type { ModuleLocator, ModuleName, Modules, AsyncModules } from './types';

export const asyncModuleLoaders: AsyncModules = {
    restrictions: loadRestrictionModule,
    betslip: loadBetslipModule,
    betsHistory: loadBetsHistoryModule,
    outright: loadOutrightModule,
    match: loadMatchModule,
    freebet: loadFreebetModule,
    search: loadSearchModule,
    stream: loadStreamModule,
    filters: loadFiltersModule,
    'bets-history': loadBetsHistoryModule,
};

export function useAsyncModule<T extends ModuleName>(
    moduleName: T,
    preload?: boolean
): Modules[T]['default'] | null {
    const [module, setModule] = useState<Modules[T]['default'] | null>(
        moduleLocator.getImmediately<T>(moduleName)
    );

    useEffect(() => {
        if (!module && preload) {
            asyncModuleLoaders[moduleName]() as Promise<Modules[T]>;
        }
    }, [module, moduleName, preload]);

    useEffect(() => {
        if (module) return;

        const sub = moduleLocator
            .get<T>(moduleName)
            .distinctUntilChanged()
            .subscribe((subModule: Modules[T]['default']) => {
                setModule(subModule);
            });

        return () => sub.unsubscribe();
    }, [moduleName, module]);

    return module;
}
